'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { apiGetAllNews } from '@/services/newsHome/api';
import { formatDateVN } from '@/utils/client/date';

interface Article {
    id: string;
    title: string;
    description: string;
    image: string;
    date: string;
    tag: string;
}

interface RoviArticlesProps {
    companyId?: string | number;
}

export default function RoviArticles({ companyId }: RoviArticlesProps) {
    const params = useParams();
    const finalId = companyId ? String(companyId) : (params.id as string);

    /* Lấy danh sách bài báo từ API */
    const { data: newsData, isLoading } = useQuery({
        queryKey: ['articles', finalId], // Dùng finalId
        queryFn: async () => {
            const res = await apiGetAllNews({
                keywords: '',
                newsTypeId: Number(finalId), // Dùng finalId gọi API
                isPublic: '',
                page: 1,
                pageSize: 100,
            });
            return res?.isOK ? res.result.items : [];
        },
        enabled: !!finalId && !isNaN(Number(finalId)),
        staleTime: 5 * 60 * 1000,
    });

    // --- 1. Hàm tạo Slug chuẩn ---
    const createSlug = (title: string) => {
        return title.toLowerCase()
            .trim()
            .replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a')
            .replace(/[éèẻẽẹêếềểễệ]/g, 'e')
            .replace(/[iíìỉĩị]/g, 'i')
            .replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o')
            .replace(/[úùủũụưứừửữự]/g, 'u')
            .replace(/[ýỳỷỹỵ]/g, 'y')
            .replace(/đ/g, 'd')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };

    /* Chuẩn hóa dữ liệu hiển thị */
    const allArticles: Article[] = isLoading || !newsData?.length
        ? []
        : newsData
            .filter((item: any) => item.seoDescription === 'Bài báo')
            .map((item: any) => ({
                id: item.id.toString(),
                title: item.title,
                description: item.description || '',
                image: item.image || '',
                date: formatDateVN(item.updatedDateTS),
                tag: item.newsType?.title,
            }));

    if (allArticles.length === 0) {
        return null;
    }

    /* Chỉ hiển thị 3 bài đầu tiên */
    const displayArticles = allArticles.slice(0, 3);

    return (
        <section className="w-full bg-white py-16" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            <div className="mx-auto max-w-[60rem] px-4 sm:px-6 lg:px-8">

                {/* Header */}
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-[#2D3746] mb-3">
                        Các bài báo của {allArticles[0]?.tag}
                    </h2>
                    <p
                        className="text-[#2D3746] text-sm"
                        style={{ fontFamily: '"Helvetica Neue", Roboto, sans-serif' }}
                    >
                        Tổng hợp những thông tin, phân tích và hoạt động nổi bật của {allArticles[0]?.tag}
                    </p>
                </div>

                {/* Danh sách bài báo */}
                <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
                    {displayArticles.map((article) => {
                        const slugTitle = createSlug(article.title);
                        const articleHref = `/news/${slugTitle}-${article.id}`;

                        return (
                            <Link
                                key={article.id}
                                href={articleHref}
                                className="group flex flex-col h-full cursor-pointer"
                            >
                                <div className="relative w-full aspect-[16/10] overflow-hidden rounded-lg mb-4 block">
                                    <div className="absolute top-4 left-4 z-10 bg-[#006633] text-[#FFFFFF] text-[11px] font-medium px-3 py-1 rounded-full shadow-sm">
                                        {article.tag}
                                    </div>

                                    <Image
                                        src={article.image || '/placeholder-article.jpg'}
                                        alt={article.title}
                                        fill
                                        unoptimized={true}
                                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                </div>

                                <div className="flex flex-col flex-1">
                                    <div className="text-xs text-[#2D3746] mb-2 font-medium">
                                        {article.date}
                                    </div>

                                    <h3
                                        className="text-base font-bold text-[#2D3746] leading-snug mb-2 line-clamp-2 group-hover:text-[#006633] transition-colors duration-300"
                                        style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
                                        title={article.title}
                                    >
                                        {article.title}
                                    </h3>

                                    <p
                                        className="text-[12px] text-[#2D3746] leading-relaxed line-clamp-3 whitespace-pre-line"
                                        style={{ fontFamily: '"Helvetica Neue", Roboto, sans-serif' }}
                                    >
                                        {article.description}
                                    </p>
                                </div>
                            </Link>
                        )
                    })}
                </div>
            </div>
        </section>
    );
}